import type { Ref } from "vue";
import { computed, onBeforeUnmount, watch } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import moment from "moment";
import type { SendChatMessageInput } from "@phurshell/psiapp-frontend-core/domain";
import { chatRepo } from "@/di/repositories";
import { pusherService } from "@/di/pusher";
import { useSessionStore } from "@/store/useSessionStore";
import { UserAvatar } from "@/constants";

function getFormattedDate(date: string, showTodayAsString = false) {
  if (moment(date).isSame(moment(), "day")) {
    return showTodayAsString ? "Hoje" : moment(date).format("HH:mm");
  } else if (moment(date).isSame(moment().add(-1, "days"), "day")) {
    return "Ontem";
  }
  return moment(date).format("DD/MM/YYYY");
}

function scrollToBottom() {
  const chatCardBody = document.getElementById("chat_card_body");
  const chatModalBody = document.getElementById("chat_modal_body");
  setTimeout(() => {
    if (chatCardBody) chatCardBody.scrollTop = chatCardBody.scrollHeight;
    if (chatModalBody) chatModalBody.scrollTop = chatModalBody.scrollHeight;
  }, 100);
}

export function useChatList() {
  const { data, isPending } = useQuery({
    queryKey: ["chats", "list"],
    queryFn: () => chatRepo.getChats(),
  });
  return { chats: computed(() => data.value ?? []), isPending };
}

export function useInvalidateChats() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["chats", "list"] });
}

export function useChatRoom(
  chatId: Ref<number | undefined>,
  chatDate: Ref<string | undefined>,
) {
  const queryClient = useQueryClient();
  const sessionStore = useSessionStore();

  const messagesQueryKey = computed(() => ["chat", "messages", chatId.value]);

  const { data: messagesData, isPending: loadingChat } = useQuery({
    queryKey: messagesQueryKey,
    queryFn: () => chatRepo.getMessages(chatId.value!),
    enabled: computed(() => !!(chatId.value && chatDate.value)),
  });

  const sectionItems = computed(() => {
    const items: any[] = (messagesData.value ?? []).map((elem) => ({
      ...elem,
      created_at: elem.date,
      user_id: elem.user?.id,
      image:
        elem.user?.id == sessionStore.session?.id
          ? `${process.env.VUE_APP_IMG_BASE_URL}${elem.user?.profile_image}`
          : UserAvatar[elem.user?.profile_image as any],
      name: elem.user?.name,
      time: moment(elem.date).format("HH:mm"),
    }));

    items.push({ isDefault: true, created_at: chatDate.value });

    const sections: any[] = [];
    items.forEach((elem, index) => {
      const currDate = moment(elem.created_at).format("YYYY-MM-DD");
      const nextDate = items[index + 1]
        ? moment(items[index + 1].created_at).format("YYYY-MM-DD")
        : null;
      const title = getFormattedDate(currDate, true);

      sections.push({ ...elem, title });

      if (currDate !== nextDate && !elem.isDefault) {
        sections.push({ id: elem.created_at, isHeader: true, title });
      }
    });

    return sections.reverse();
  });

  watch(sectionItems, () => {
    scrollToBottom();
  });

  const formatted_date = computed(() =>
    chatDate.value ? moment(chatDate.value).format("DD/MM/YYYY") : "",
  );

  const invalidateMessages = () => {
    queryClient.invalidateQueries({ queryKey: messagesQueryKey.value });
    queryClient.invalidateQueries({ queryKey: ["chats", "list"] });
  };

  let channel: any = null;

  const setupPusher = (id: number) => {
    channel = pusherService.subscribe(`appointment_chat.${id}`);
    channel.bind("new-message", (event: any) => {
      if (event.message.user_id !== sessionStore.session?.id) {
        invalidateMessages();
      }
    });
  };

  watch(
    chatId,
    (newId) => {
      if (newId) setupPusher(newId);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    if (chatId.value) {
      pusherService.unsubscribe(`appointment_chat.${chatId.value}`, "new-message");
    }
  });

  return { sectionItems, loadingChat, formatted_date, invalidateMessages };
}

export function useSendChatMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: SendChatMessageInput) => chatRepo.sendMessage(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "messages"] });
      queryClient.invalidateQueries({ queryKey: ["chats", "list"] });
    },
  });
}

export function useEditChatMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, message }: { id: number | string; message: string }) =>
      chatRepo.editMessage(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "messages"] });
    },
  });
}

export function usePinChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => chatRepo.pinChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats", "list"] });
    },
  });
}

export function useArchiveChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => chatRepo.archiveChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats", "list"] });
    },
  });
}
